import { useQuery } from "@tanstack/react-query";
import { ArrowLeftRight, PackageX, TriangleAlert } from "lucide-react";

import { apiError } from "../../../shared/api/client";
import {
  substitutionApi,
  substitutionQueryKeys,
  type MedicineSubstitute,
} from "../../../shared/api/medicines";
import { formatVnd } from "../../../shared/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Thuốc thay thế cùng hoạt chất — pick an alternative without leaving the prescription (#substitution).
 *
 * <p>Shows what differs from the medicine being replaced rather than hiding it. Same active
 * ingredient is not the same medicine: strength, form and route each change the dose, so a
 * mismatch is surfaced as a warning the prescriber must read, and choosing is always a click a
 * human makes. The backend never auto-substitutes either.
 */
export function SubstituteMedicineDialog({
  medicineId,
  medicineName,
  branchId,
  onPick,
  onClose,
}: {
  medicineId: string;
  medicineName: string;
  branchId?: string | null;
  onPick: (s: MedicineSubstitute) => void;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: substitutionQueryKeys.forMedicine(medicineId, branchId),
    queryFn: () => substitutionApi.forMedicine(medicineId, branchId),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Thuốc thay thế cho ${medicineName}`}
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-background p-5 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center gap-2">
          <ArrowLeftRight className="size-4" />
          <h2 className="text-sm font-semibold">Thuốc thay thế cùng hoạt chất</h2>
        </div>
        <p className="mb-3 text-sm text-muted-foreground">
          Thay cho <strong className="text-foreground">{medicineName}</strong>
          {q.data && (
            <>
              {" "}· tồn kho hiện tại{" "}
              <strong className={q.data.inStock ? "text-foreground" : "text-destructive"}>
                {q.data.stockOnHand ?? 0}
              </strong>
            </>
          )}
        </p>

        {q.isLoading && <Skeleton className="h-24" />}
        {q.isError && <p className="text-sm text-destructive">{apiError(q.error).detail}</p>}

        {q.data?.substitutes.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-muted-foreground">
            <PackageX className="size-7" />
            <p className="text-sm">
              Không tìm thấy thuốc nào cùng hoạt chất trong danh mục của phòng khám.
            </p>
          </div>
        )}

        <ul className="divide-y">
          {q.data?.substitutes.map((s) => (
            <li key={s.medicineId} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <strong className="text-sm">{s.name}</strong>
                  {s.strength && (
                    <span className="text-sm text-muted-foreground">· {s.strength}</span>
                  )}
                  {s.inStock ? (
                    <Badge>Còn {s.stockOnHand}</Badge>
                  ) : (
                    <Badge variant="secondary">Hết hàng</Badge>
                  )}
                  {s.isControlled && <Badge variant="destructive">Thuốc kiểm soát</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {s.activeIngredient}
                  {s.dosageForm ? ` · ${s.dosageForm}` : ""}
                  {s.routeOfAdministration ? ` · ${s.routeOfAdministration}` : ""}
                  {s.sellPriceVnd ? ` · ${formatVnd(s.sellPriceVnd)}₫` : ""}
                </div>
                {/* Named explicitly, because these are the differences that change the dose. */}
                {differences(s).length > 0 && (
                  <div className="mt-1 flex items-start gap-1 text-xs text-amber-600">
                    <TriangleAlert className="mt-0.5 size-3 shrink-0" />
                    <span>Khác {differences(s).join(", ")} — cần xem lại liều.</span>
                  </div>
                )}
              </div>
              <Button
                type="button"
                size="sm"
                variant={s.inStock ? "default" : "outline"}
                onClick={() => {
                  onPick(s);
                  onClose();
                }}
              >
                Chọn
              </Button>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Đóng
          </Button>
        </div>
      </div>
    </div>
  );
}

function differences(s: MedicineSubstitute): string[] {
  const out: string[] = [];
  if (!s.sameStrength) out.push("hàm lượng");
  if (!s.sameDosageForm) out.push("dạng bào chế");
  if (!s.sameRoute) out.push("đường dùng");
  return out;
}
